/**
 * Contrato del brief del Estudio de marketing.
 *
 * Estos tipos son el BORRADOR de la tabla `content_briefs` del Bloque F: la
 * forma que se fije acá se arrastra hasta allá. Especificación completa y
 * ejemplos en `docs/superpowers/specs/2026-08-16-estudio-marketing-contrato-brief.md`.
 */

export type Canal = "instagram" | "meta_ads";
export type FormatoPieza = "reel_9x16" | "feed_1x1" | "feed_4x5" | "carrusel_4x5";

export interface Coordenada {
  persona: string;
  angulo: string;
  formato: FormatoPieza;
  canal: Canal;
}

/**
 * Un número del cliente que respalda una propuesta.
 *
 * `fila_origen` es el campo que hace que esto valga: obliga a que el número
 * apunte a una fila que existe. Sin él, un agente puede inventar una cifra que
 * suena igual de convincente que una real (ADR-2026-08-16-brief-respaldo-estructurado).
 */
export interface ItemRespaldo {
  metrica: string;
  valor: number;
  /** Valor contra el que se compara, si la métrica es comparativa. */
  comparado_con?: number;
  /** Qué es ese otro valor, en castellano. Ej. "fotos de catálogo". */
  contra_que?: string;
  ventana: string;
  fuente: "instagram" | "meta_ads" | "tiendanube" | "atlas";
  /** Id de la fila exacta de la que salió el número. Obligatorio. */
  fila_origen: string;
}

export type EstadoPropuesta = "listo" | "generando" | "propuesta" | "en_observacion";

export interface Propuesta {
  pieza_id: string;
  titulo: string;
  estado: EstadoPropuesta;
  coordenada: Coordenada;
  por_que: string;
  respaldo: ItemRespaldo[];
  tipo: "explotacion" | "exploracion";
  /** Qué dato le falta al estratega para estar seguro. Se muestra al usuario. */
  duda: string | null;
  confianza: number;
  riesgo: "bajo" | "medio" | "alto";
  creditos_estimados: number | null;
}

export interface Beat {
  n: number;
  desde_s: number;
  hasta_s: number;
  texto_en_pantalla: string;
  voz: string | null;
  que_se_entiende: string;
  /** Palabras de subtítulo a la vez. Vive por beat: el hook va rápido y la
   *  parte explicativa lenta, en el mismo video. */
  densidad_subtitulo: number;
}

export interface Plano {
  id: string;
  beat: number;
  tipo: "imagen_fija" | "clip";
  descripcion: string;
  /** Planos que usan a este como referencia. Es una arista del grafo de generación. */
  referencia_de?: string[];
  /** Plano que este usa como referencia. */
  usa_referencia?: string | null;
  lleva_voz?: boolean;
}

export type NombreEtapa =
  | "estrategia"
  | "direccion_creativa"
  | "guion"
  | "plan_de_planos"
  | "assets"
  | "armado";

export interface MetaEtapa {
  estado: "pendiente" | "listo" | "editado_por_usuario" | "fallo";
  producido_por: string;
  modelo: string | null;
  /** Con qué versión del prompt se generó. Sin esto, una pieza que sale mal es
   *  imposible de atribuir a un cambio de prompt. */
  prompt_version: string | null;
  cuando: string;
}

export interface Brief {
  pieza_id: string;
  version: number;
  sale_de_version: number | null;
  la_escribio: string;
  cuando: string;
  coordenada: Coordenada;
  estrategia: {
    _meta: MetaEtapa;
    por_que: string;
    respaldo: ItemRespaldo[];
    tipo: "explotacion" | "exploracion";
    producto_id: string | null;
    confianza: number;
    duda: string | null;
    riesgo: "bajo" | "medio" | "alto";
  };
  direccion_creativa: {
    _meta: MetaEtapa;
    direccion_visual: string;
    reglas_de_marca_aplicadas: string[];
    avatar_id: string | null;
  };
  guion: {
    _meta: MetaEtapa;
    hook: string;
    beats: Beat[];
    cta: string;
  };
  plan_de_planos: {
    _meta: MetaEtapa;
    planos: Plano[];
    total_planos: number;
    con_movimiento: number;
  };
  assets: {
    _meta: MetaEtapa;
    grafo_id: string | null;
    resumen: Array<{
      plano: string;
      estado: "aprobado" | "rechazado" | "pendiente";
      intentos: number;
      creditos: number;
      rechazo?: string;
    }>;
    creditos_totales: number;
  };
  armado: {
    _meta: MetaEtapa;
    duracion_s: number;
    relacion: string;
    subtitulos_quemados: boolean;
    musica: string | null;
    area_segura_verificada: boolean;
  };
  copy_de_canal: {
    _meta: MetaEtapa;
    instagram_feed?: { caption: string; hashtags: string[] };
    meta_ads?: { titulo: string; texto_principal: string; descripcion: string };
  };
}

/**
 * Una propuesta solo se puede mostrar si su respaldo es verificable: al menos un
 * número, con valor finito y con la fila de la que salió.
 *
 * En el Bloque A esto valida la FORMA. Cuando exista la base, la misma función
 * es el lugar donde se agrega la verificación contra la fila real.
 */
export function respaldoEsVerificable(respaldo: ItemRespaldo[]): boolean {
  if (respaldo.length === 0) return false;
  return respaldo.every(
    (item) =>
      typeof item.fila_origen === "string" &&
      item.fila_origen.trim().length > 0 &&
      Number.isFinite(item.valor) &&
      item.metrica.trim().length > 0,
  );
}

/**
 * Los beats tienen que cubrir la línea de tiempo sin huecos ni superposiciones.
 * Un hueco significa segundos de video sin nada definido; una superposición,
 * dos textos en pantalla al mismo tiempo.
 */
export function beatsSonContiguos(beats: Beat[]): boolean {
  if (beats.length === 0) return true;
  const ordenados = [...beats].sort((a, b) => a.desde_s - b.desde_s);
  for (let i = 0; i < ordenados.length; i += 1) {
    const beat = ordenados[i];
    if (!(beat.hasta_s > beat.desde_s)) return false;
    if (i > 0 && beat.desde_s !== ordenados[i - 1].hasta_s) return false;
  }
  return true;
}
